import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, CopyPlus, Layers, StickyNote, UserRoundPlus, UserRoundX } from 'lucide-react';
import { cn } from '@/lib/utils';
import { RowActions } from '@/components/ui/row-actions';
import { StatusPill } from '@/components/ui/status-pill';
import { Avatar } from '@/components/ui/avatar-initials';
import {
  AwaitingNote,
  DueBadge,
  NeedsSetup,
  projectMenuItems,
  StageBar,
  SubLine,
  subParts,
  SystemChip,
} from './ProjectCells';
import { cleanText, matchesStatusTab, newProjectLabel, projectHref, projectStatusLabel, stageSummaryOf } from './projectsModel';

/*
  Phones and tablets (03 #4): the same data as the table, as stacked cards — system chip + name + a 44px ⋯,
  the place, the stage with a full-width 10-step bar, then the engineer, due date and status. A site with
  several systems is one card ("Nobu Hotel · 4 systems") that opens to its projects (03 #7).
  `columns={2}` puts two cards per row (a tablet in landscape: the list is too narrow for the table).
*/

function StageBlock({ project }) {
  const summary = stageSummaryOf(project);
  if (!summary) return null;
  return (
    <div className="flex flex-col gap-1.5 border-t border-line-2 pt-2.5">
      <div className="flex items-center justify-between gap-2 text-[13px] font-semibold text-ink">
        <span className="min-w-0 truncate">{summary.complete ? 'All stages done' : summary.current_stage}</span>
        {!summary.complete ? <span className="shrink-0 text-[12px] font-medium text-ink-3">{summary.current_index}/{summary.count}</span> : null}
      </div>
      <StageBar steps={summary.steps} size="lg" />
      <AwaitingNote summary={summary} className="text-[12.5px]" />
    </div>
  );
}

function PersonLine({ project, onAssign }) {
  const engineer = cleanText(project.project_engineer) || cleanText(project.senior_project_engineer);
  if (engineer) {
    return (
      <span className="flex min-w-0 items-center gap-2" data-testid="project-team">
        <Avatar name={engineer} size={24} />
        <span className="truncate text-[13px] font-semibold text-ink-2">{engineer}</span>
      </span>
    );
  }
  if (onAssign && matchesStatusTab(project, 'Active')) {
    return (
      <button
        type="button"
        onClick={() => onAssign(project)}
        className="-mx-1 flex min-h-[36px] min-w-0 items-center gap-2 rounded-lg px-1 text-[13px] font-semibold text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={`Assign a Project Engineer to ${project.name}`}
        data-testid="project-assign-engineer"
      >
        <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full border-[1.5px] border-dashed border-[color:var(--brand-soft-border)]">
          <UserRoundPlus className="h-3 w-3" aria-hidden="true" />
        </span>
        Assign engineer
      </button>
    );
  }
  return <span className="text-[13px] text-ink-3">Unassigned</span>;
}

export function ProjectCard({ project, menu, onEdit, canEdit, now, nested = false }) {
  const newLabel = newProjectLabel(project, { now });
  const notes = cleanText(project.notes);
  const parts = subParts(project);
  return (
    <article
      className={cn('flex flex-col gap-2 rounded-card border border-line bg-surface p-3.5 shadow-card', nested && 'shadow-none')}
      data-testid={`project-card-${project.id}`}
    >
      <div className="-mr-2 -mt-1.5 flex min-w-0 items-center gap-2">
        <SystemChip systemType={project.system_type} />
        <Link
          to={projectHref(project)}
          className="min-w-0 truncate rounded-sm text-[16px] font-bold leading-tight text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid={`project-link-${project.id}`}
        >
          {project.name || 'Untitled project'}
        </Link>
        {newLabel ? <StatusPill tone="lime" label={newLabel} size="sm" hideDot className="shrink-0" data-testid="project-new-badge" /> : null}
        <RowActions
          label={`Actions for ${project.name}`}
          items={menu}
          size="default"
          className="ml-auto h-11 w-11 shrink-0 text-ink-3"
          data-testid={`project-actions-${project.id}`}
        />
      </div>
      {parts.length ? <div className="-mt-1 flex min-w-0 text-[13px] text-ink-3"><SubLine parts={parts} /></div> : null}
      {notes ? (
        <div className="relative -mt-0.5 flex min-w-0 items-center gap-1.5 text-[12.5px] text-ink-3" title={notes} data-testid="project-notes-indicator">
          <StickyNote className="h-[13px] w-[13px] shrink-0 text-ink-2" aria-hidden="true" />
          <span className="sr-only">Notes: </span>
          <span className="min-w-0 truncate">{notes}</span>
        </div>
      ) : null}
      <NeedsSetup project={project} onComplete={canEdit ? onEdit : undefined} className="text-[12.5px]" />
      <StageBlock project={project} />
      <div className="flex min-w-0 items-center gap-2">
        <PersonLine project={project} onAssign={canEdit ? onEdit : undefined} />
        <span className="ml-auto flex shrink-0 items-center gap-2">
          <DueBadge project={project} now={now} />
          <StatusPill status={project.status} domain="project" label={projectStatusLabel(project.status) || undefined} size="sm" data-testid="project-status" />
        </span>
      </div>
    </article>
  );
}

function GroupCard({ entry, expanded, onToggle, onAssign, onDuplicate, canEdit, renderProject }) {
  const { group, info } = entry;
  const place = [group.client, info.placeText].filter(Boolean).join(' · ');
  return (
    <article className="flex flex-col gap-2 rounded-card border border-line bg-surface p-3.5 shadow-card" data-testid="project-group-card">
      <div className="-mr-2 -mt-1.5 flex min-w-0 items-center gap-2">
        <span className="ui-icon-tile tone-navy h-[34px] w-[34px] rounded-[10px]" aria-hidden="true">
          <Layers className="h-[17px] w-[17px]" />
        </span>
        <span className="min-w-0 truncate text-[16px] font-bold leading-tight text-ink">{group.name}</span>
        <StatusPill tone="navy" label={`${info.count} systems`} size="sm" hideDot className="shrink-0" />
        <RowActions
          label={`Actions for ${group.name}`}
          size="default"
          className="ml-auto h-11 w-11 shrink-0 text-ink-3"
          items={[
            { label: expanded ? `Hide the ${info.count} projects` : `Show the ${info.count} projects`, icon: expanded ? ChevronDown : ChevronRight, onSelect: onToggle },
            canEdit && { label: 'Add another system…', icon: CopyPlus, onSelect: () => onDuplicate(group.projects[0]) },
          ]}
        />
      </div>
      {place || info.missingClient ? (
        <div className="-mt-1 min-w-0 truncate text-[13px] text-ink-3">
          {place}
          {info.missingClient ? <span className="font-semibold text-status-amber-ink">{place ? ' · ' : ''}{info.missingClient} missing client</span> : null}
        </div>
      ) : null}
      <div className="flex flex-wrap gap-1">
        {group.projects.map((project) => <SystemChip key={project.id} systemType={project.system_type} size="sm" className="h-5 px-1.5 text-[10px]" />)}
      </div>
      {info.unassigned ? (
        <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-status-amber-ink">
          <UserRoundX className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          No engineer on {info.unassigned === info.count ? `${info.count} projects` : `${info.unassigned} of ${info.count}`}
          {canEdit && onAssign ? (
            <button
              type="button"
              onClick={() => onAssign(group.projects.filter((project) => !cleanText(project.project_engineer)))}
              className="ml-auto min-h-[32px] rounded px-1 text-[13px] font-semibold text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Assign
            </button>
          ) : null}
        </div>
      ) : null}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="-mx-3.5 -mb-3.5 mt-0.5 flex h-11 items-center justify-between rounded-b-card border-t border-line-2 px-3.5 text-[13.5px] font-bold text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        data-testid="project-group-toggle"
      >
        {expanded ? `Hide ${info.count} projects` : `View ${info.count} projects`}
        {expanded ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
      </button>
      {expanded ? <div className="mt-3.5 flex flex-col gap-2">{group.projects.map((project) => renderProject(project, true))}</div> : null}
    </article>
  );
}

/** entries: [{ kind: 'project', project } | { kind: 'group', group, info }] (already sorted). */
export function ProjectCards({
  entries, expandedGroups, onToggleGroup, canEdit, canDelete, onEdit, onDuplicate, onDelete, onCopyLink, onAssignMany, now, columns = 1,
}) {
  const navigate = useNavigate();
  const renderProject = (project, nested = false) => (
    <ProjectCard
      key={project.id}
      project={project}
      nested={nested}
      canEdit={canEdit}
      onEdit={onEdit}
      now={now}
      menu={projectMenuItems(project, { navigate, canEdit, canDelete, onEdit, onDuplicate, onDelete, onCopyLink })}
    />
  );
  return (
    <div className={columns === 2 ? 'grid grid-cols-2 items-start gap-2.5' : 'flex flex-col gap-2.5'} data-testid="projects-list">
      {entries.map((entry) => (entry.kind === 'group' ? (
        <GroupCard
          key={entry.group.key}
          entry={entry}
          expanded={expandedGroups.has(entry.group.key)}
          onToggle={() => onToggleGroup(entry.group.key)}
          onAssign={onAssignMany}
          onDuplicate={onDuplicate}
          canEdit={canEdit}
          renderProject={renderProject}
        />
      ) : renderProject(entry.project)))}
    </div>
  );
}

export default ProjectCards;
